import { useEffect, useState } from "react";
import { mediaCache } from "@/utils/media-cache";

type MediaType = "video" | "audio" | "image";

/**
 * Custom hook for downloading and caching media assets
 * Handles download, error handling, cleanup, and AbortController management
 *
 * The returned URL is guaranteed to be a LIVE blob URL for the CURRENT `url`.
 * Two things can make a blob URL dead, and both return null here instead:
 * the render where `url` changes (the resolved blob still belongs to the
 * previous asset and is about to be revoked), and any later render after this
 * hook revoked the blob it was holding (revisiting an earlier url). A consumer
 * that captures a dead blob: URL gets net::ERR_FILE_NOT_FOUND and the media
 * element fails with NotSupportedError on play.
 *
 * @param url - The URL of the media asset to download
 * @param type - The type of media (video, audio, or image)
 * @returns The cached blob URL (or original URL if download fails), or null
 *          while the current url's download is still in flight
 */
export function useMediaCache(
  url: string | null,
  type: MediaType,
): string | null {
  const [resolved, setResolved] = useState<{
    forUrl: string;
    blobUrl: string;
  } | null>(null);

  useEffect(() => {
    if (!url) {
      setResolved(null);
      return;
    }

    // Create AbortController to cancel download if component unmounts or url changes
    const abortController = new AbortController();
    let createdBlobUrl: string | null = null;

    mediaCache
      .downloadAndCache(url, type, abortController.signal)
      .then((cachedUrl) => {
        // Only update state if not aborted
        if (!abortController.signal.aborted) {
          createdBlobUrl = cachedUrl;
          setResolved({ forUrl: url, blobUrl: cachedUrl });
        }
      })
      .catch((error) => {
        // Don't log error if it was aborted (expected behavior)
        if (error instanceof Error && error.name === "AbortError") {
          return; // Silently ignore abort
        }
        console.error(`Error downloading ${type}:`, error);
        // Fallback to original URL only if not aborted
        if (!abortController.signal.aborted) {
          setResolved({ forUrl: url, blobUrl: url });
        }
      });

    // Cleanup: cancel download and revoke blob URL when component unmounts or url changes
    return () => {
      abortController.abort(); // Cancel ongoing download
      if (createdBlobUrl && createdBlobUrl.startsWith("blob:")) {
        mediaCache.revokeBlobUrl(createdBlobUrl);

        // Drop the state that points at the URL we just revoked. Without
        // this, going back to a url we showed before (stage A -> B -> A, or
        // a repeated vocabulary cue) would pass the forUrl check below and
        // hand out a dead blob: URL again.
        setResolved((current) =>
          current && current.blobUrl === createdBlobUrl ? null : current,
        );
      }
    };
  }, [url, type]);

  // Never hand out a blob URL that belongs to a different url.
  return resolved && resolved.forUrl === url ? resolved.blobUrl : null;
}
